import { Company, Reservation, ReservationSource, Settings, Table } from '@backend/domain'
import { CompanyRepository, ReservationRepository, SettingsRepository, TableRepository } from '@backend/repository'
import { IWeeklySchedule, ReservationState, StageId } from '@core/types'
import { removeNulls } from '@core/utils'
import { Injectable, Logger } from '@nestjs/common'
import { addDays, addMinutes, format } from 'date-fns'
import { randomInt, range, uniq } from 'es-toolkit'
import { In, Not, Raw } from 'typeorm'
import { Cron } from '../../../base'
import { Stage } from '../../../config'
import { TimingRule } from '../../settings'

const EXCLUDED_RESTAURANTS = ['4e22f973-36d5-4e8c-b8a3-c85a7b87cf10']

/** Fake reservations seeder for restaurants that have Google reservations enabled */
@Injectable()
export class ReservationSeederCron {
	private readonly logger = new Logger(ReservationSeederCron.name)

	private restaurants: Company[] = []
	private settingsMap: Map<string, Settings> = new Map()
	private tablesMap: Map<string, Table[]> = new Map()

	constructor(
		private readonly _tableRepository: TableRepository,
		private readonly _companyRepository: CompanyRepository,
		private readonly _settingsRepository: SettingsRepository,
		private readonly _reservationRepository: ReservationRepository,
	) {}

	@Cron({
		name: 'reservation_seeder',
		schedule: '0 0 3 * * *',
		appconfigScheduleKey: 'reservation_seeder_cron_schedule',
	})
	async seedReservations() {
		if (Stage !== StageId.PRODUCTION) {
			this.logger.log('Skipping reservation seeder because stage in not `production`')
			return
		}

		await this.loadRestaurants()

		if (this.restaurants.length === 0) {
			this.logger.log('No restaurants with Google reservations enabled, skipping reservations seeding')
			return
		}

		for (const restaurant of this.restaurants) {
			await this.seedReservationsForRestaurant(restaurant)
		}

		this.logger.log('Seeded reservations for all restaurants')
	}

	private async seedReservationsForRestaurant(restaurant: Company): Promise<void> {
		const settings = this.settingsMap.get(restaurant.id)
		if (!settings) {
			this.logger.error(`Settings not found for tenant`, { tenantId: restaurant.id })
			return
		}

		if (!settings.schedule?.weeklySchedule) {
			this.logger.warn(`No schedule found for restaurant`, { tenantId: restaurant.id })
			return
		}

		const tables = this.tablesMap.get(restaurant.id)
		if (!tables || tables.length === 0) {
			this.logger.warn('No tables found for restaurant', { tenantId: restaurant.id })
			return
		}

		// Seed between 4 and 10 reservations per restaurant
		const numberOfReservations = randomInt(4, 10)

		const today = new Date()
		today.setHours(0, 0, 0, 0)

		const reservations: Partial<Reservation>[] = []
		const maxCapacity = Math.max(...tables.map((table) => table.capacity))

		for (let i = 0; i < numberOfReservations; i++) {
			// Seed reservations within 28 days window
			const reservationDate = addDays(today, randomInt(0, 28))

			const dayName = format(reservationDate, 'EEEE')
			const daySchedule = settings.schedule.weeklySchedule[dayName as keyof IWeeklySchedule]

			const validHours = this.getValidHoursFromSchedule(daySchedule.rules)
			if (validHours.length === 0) continue

			const startHour = randomInt(validHours[0], validHours[validHours.length - 1])
			reservationDate.setHours(startHour)

			const startDate = new Date(reservationDate)
			const endDate = addMinutes(startDate, settings.reservation.defaultReservationTime)

			const noOfPersons = randomInt(1, maxCapacity)
			const availableTables = tables.filter((table) => table.capacity >= noOfPersons)

			const table =
				availableTables.length > 1 ? availableTables[randomInt(0, availableTables.length - 1)] : availableTables[0]

			reservations.push({
				tenantId: restaurant.id,
				noOfPersons,
				startDate,
				endDate,
				customerData: { name: 'System generated' },
				comment: `Seeded at ${new Date().toISOString()}`,
				source: ReservationSource.admin,
				state: ReservationState.approved,
				tables: [table],
			})
		}

		await this._reservationRepository.save(reservations)

		this.logger.log(`Seeded ${reservations.length} reservations for ${restaurant.name}`)
	}

	private getValidHoursFromSchedule(rules: TimingRule[]): number[] {
		const hours: number[] = []

		for (const rule of rules) {
			hours.push(...range(rule.openingTime.hours, rule.closingTime.hours))
		}

		return uniq(hours)
	}

	private async loadRestaurants(): Promise<void> {
		const settings = await this._settingsRepository.find({
			where: {
				reservation: Raw((alias) => `${alias} -> 'googleReservations' ->> 'enabled' = 'true'`),
				tenantId: Not(In(EXCLUDED_RESTAURANTS)),
			},
		})

		if (settings.length === 0) return

		const restaurantIds = settings.map((setting) => setting.tenantId).filter(removeNulls)

		this.restaurants = await this._companyRepository.find({
			where: { id: In(restaurantIds) },
		})
		this.settingsMap = new Map(settings.map((setting) => [setting.tenantId as string, setting]))

		const tables = await this._tableRepository.find({
			where: { tenantId: In(restaurantIds) },
		})
		this.tablesMap = new Map(
			tables.map((table) => [table.tenantId as string, tables.filter((_table) => _table.tenantId === table.tenantId)]),
		)

		this.logger.log({
			message: `Seeding fake reservations for ${this.restaurants.length} restaurants`,
			restaurants: this.restaurants.map((restaurant) => restaurant.name),
			count: this.restaurants.length,
		})
	}
}
